import { useAutoneTranslation } from '@autone/translations';
import {
  createColumnLoadingGuards,
  DataTableCellContent,
  DataTableCellCurrencyValue,
  DataTableCellTitle,
  DataTableHeaderCellWithSorting,
  type DataTableLoadingObject,
} from '@autone/ui';
import { type ColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';

import { type APP_NAME } from '../../../consts';
import { useAssortmentTableContext } from '../contexts';

import { type CommonAssortmentTableItem } from './common-columns.types';

const COLUMN_ID = 'total_sales_value' as const;

export type TotalSalesValueColumnId = typeof COLUMN_ID;

interface TotalSalesValueFields {
  lifetime_sales_value: CommonAssortmentTableItem['lifetime_sales_value'];
}

const { columnCellGuard, accessorFnGuard } =
  createColumnLoadingGuards<TotalSalesValueFields>();

export const createTotalSalesValueColumn = <
  T extends TotalSalesValueFields | DataTableLoadingObject,
>(
  columnHelper: ColumnHelper<T>,
) =>
  columnHelper.accessor(
    accessorFnGuard((row) => row.lifetime_sales_value),
    {
      id: COLUMN_ID,
      size: 150,
      header: function TotalSalesValueHeader({ column }) {
        const { t } = useAutoneTranslation<typeof APP_NAME>();
        const tableName = useAssortmentTableContext();

        const headerCellContents = useMemo(
          () => (
            <DataTableCellContent align={'right'}>
              <DataTableCellTitle className="uppercase font-medium whitespace-normal text-right">
                {t('table-columns.total-sales-value')}
              </DataTableCellTitle>
            </DataTableCellContent>
          ),
          [t],
        );

        if (tableName === 'skuLocationsTable') return headerCellContents;

        return (
          <DataTableHeaderCellWithSorting
            column={column}
            arrowPosition={'left'}
            showSortable
          >
            {headerCellContents}
          </DataTableHeaderCellWithSorting>
        );
      },
      cell: (ctx) =>
        columnCellGuard({
          ctx,
          renderCell: function TotalSalesValueCell({ getValue }) {
            return (
              <DataTableCellContent align="right">
                <DataTableCellTitle>
                  <DataTableCellCurrencyValue value={getValue()} />
                </DataTableCellTitle>
              </DataTableCellContent>
            );
          },
        }),
    },
  );
